export type MovementMode = "free" | "lanes";

export interface GameSettings {
  movementMode: MovementMode;
  musicVolume: number;
  audioVolume: number;
  movementSensitivity: number;
  cameraSensitivity: number;
  controllerDeadzone: number;
}

const STORAGE_KEY = "wr_game_settings_v1";
export const GAME_SETTINGS_CHANGED = "wr-game-settings-changed";

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  movementMode: "free",
  musicVolume: 0.5,
  audioVolume: 1,
  movementSensitivity: 0.85,
  cameraSensitivity: 0.65,
  controllerDeadzone: 0.22,
};

function clampVolume(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

function clampSensitivity(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0.25, Math.min(1.5, n));
}

function clampDeadzone(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0.05, Math.min(0.4, n));
}

export function loadGameSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_GAME_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    return {
      movementMode: parsed.movementMode === "lanes" ? "lanes" : "free",
      musicVolume: clampVolume(parsed.musicVolume, DEFAULT_GAME_SETTINGS.musicVolume),
      audioVolume: clampVolume(parsed.audioVolume, DEFAULT_GAME_SETTINGS.audioVolume),
      movementSensitivity: clampSensitivity(parsed.movementSensitivity, DEFAULT_GAME_SETTINGS.movementSensitivity),
      cameraSensitivity: clampSensitivity(parsed.cameraSensitivity, DEFAULT_GAME_SETTINGS.cameraSensitivity),
      controllerDeadzone: clampDeadzone(parsed.controllerDeadzone, DEFAULT_GAME_SETTINGS.controllerDeadzone),
    };
  } catch {
    return DEFAULT_GAME_SETTINGS;
  }
}

export function saveGameSettings(settings: GameSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent<GameSettings>(GAME_SETTINGS_CHANGED, { detail: settings }));
  } catch {
    /* ignore private-mode storage failures */
  }
}
